// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useQuery } from "@tanstack/react-query";
import {
  addDays,
  addMonths,
  addWeeks,
  differenceInCalendarDays,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "@/api/client";
import type { UserRow } from "@/api/hooks/use-users";
import { InlineSpinner } from "@/components/shared/inline-spinner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { resolveDateLocale } from "@/lib/format";
import { useDateFormat } from "@/lib/preferences";

// The filters that currently scope the users list. Mirrors the shape passed to `useUsersQuery`
// so the chart reflects exactly what the table is showing.
export interface LastLoginFilters {
  search?: string;
  onlyActive?: boolean;
  realm?: string;
  chainName?: string;
  storeName?: string;
  group?: string;
}

interface LastLoginChartDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: LastLoginFilters;
}

// Compact tick labels (16K, 1.2K) so multi-thousand counts don't clip against the dialog edge.
const COMPACT_NUMBER = new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 });

// One stacked area per user status. Colors mirror the StatusIndicator palette (green/gray/red) via
// theme tokens so they stay correct in dark mode. Array order = stacking order, bottom → top:
// deleted at the bottom, then inactive, with active on top. Legend/tooltip read the reverse
// (top-down) so they match the visual stack.
const STATUS_SERIES = [
  { key: "deleted", color: "var(--destructive)", gradientId: "fillDeleted" },
  { key: "inactive", color: "var(--muted-foreground)", gradientId: "fillInactive" },
  { key: "active", color: "var(--primary)", gradientId: "fillActive" },
] as const;

type Status = (typeof STATUS_SERIES)[number]["key"];

function statusOf(user: UserRow): Status {
  if (user.isDeleted) return "deleted";
  if (user.isActive) return "active";
  return "inactive";
}

type Granularity = "day" | "week" | "month";

interface Bucket {
  key: number;
  label: string;
  active: number;
  inactive: number;
  deleted: number;
}

interface ChartData {
  buckets: Bucket[];
  neverCount: number;
  presentStatuses: Status[];
}

function emptyCounts() {
  return { active: 0, inactive: 0, deleted: 0 };
}

// Bucket users by the calendar period of their last login, split by status, picking a granularity
// that keeps the number of points readable: daily for short spans, weekly for up to a year,
// monthly beyond that.
function buildChartData(users: UserRow[], labelFor: (date: Date, granularity: Granularity) => string): ChartData {
  const logins: { date: Date; status: Status }[] = [];
  let neverCount = 0;
  for (const user of users) {
    const status = statusOf(user);
    if (user.lastLoginAt) logins.push({ date: parseISO(user.lastLoginAt), status });
    else neverCount++;
  }
  if (logins.length === 0) return { buckets: [], neverCount, presentStatuses: [] };

  let minTime = Number.POSITIVE_INFINITY;
  let maxTime = Number.NEGATIVE_INFINITY;
  for (const { date } of logins) {
    const time = date.getTime();
    if (time < minTime) minTime = time;
    if (time > maxTime) maxTime = time;
  }
  const min = new Date(minTime);
  const max = new Date(maxTime);

  const spanDays = differenceInCalendarDays(max, min);
  const granularity: Granularity = spanDays <= 60 ? "day" : spanDays <= 365 ? "week" : "month";

  const startOfPeriod = (date: Date) => {
    if (granularity === "day") return startOfDay(date);
    if (granularity === "week") return startOfWeek(date, { weekStartsOn: 1 });
    return startOfMonth(date);
  };
  const nextPeriod = (date: Date) => {
    if (granularity === "day") return addDays(date, 1);
    if (granularity === "week") return addWeeks(date, 1);
    return addMonths(date, 1);
  };

  const counts = new Map<number, ReturnType<typeof emptyCounts>>();
  const seen = { active: false, inactive: false, deleted: false };
  for (const { date, status } of logins) {
    const key = startOfPeriod(date).getTime();
    const bucket = counts.get(key) ?? emptyCounts();
    bucket[status] += 1;
    counts.set(key, bucket);
    seen[status] = true;
  }

  // Walk the full range so empty periods render as zero-height points (a continuous date axis).
  const buckets: Bucket[] = [];
  const end = startOfPeriod(max).getTime();
  let cursor = startOfPeriod(min);
  // Guard against pathological ranges; daily caps at 61, weekly at ~53, monthly is unbounded.
  for (let guard = 0; cursor.getTime() <= end && guard < 600; guard++) {
    buckets.push({
      key: cursor.getTime(),
      label: labelFor(cursor, granularity),
      ...(counts.get(cursor.getTime()) ?? emptyCounts()),
    });
    cursor = nextPeriod(cursor);
  }

  const presentStatuses = STATUS_SERIES.map((series) => series.key).filter((key) => seen[key]);
  return { buckets, neverCount, presentStatuses };
}

export function LastLoginChartDialog({ open, onOpenChange, filters }: LastLoginChartDialogProps) {
  const { t } = useTranslation("back-office");
  const [dateFormat] = useDateFormat();
  const statusLabel = (status: Status) => t(`status.${status}`, { ns: "general" });

  // Animate the intro once per open, then freeze so resizing/hovering doesn't re-animate the areas.
  const [animate, setAnimate] = useState(true);

  const { data: users, isLoading } = useQuery({
    queryKey: ["users", "last-login", filters],
    enabled: open,
    queryFn: async () => {
      const res = await api.GET("/v1/users", {
        params: {
          query: {
            page: 1,
            limit: -1,
            search: filters.search || undefined,
            "filter[onlyActive]": filters.onlyActive,
            "filter[realm]": filters.realm || undefined,
            "filter[chainName]": filters.chainName || undefined,
            "filter[storeName]": filters.storeName || undefined,
            "filter[group]": filters.group || undefined,
          },
        },
      });
      return (res.data as unknown as { users: UserRow[] }).users;
    },
  });

  // Axis labels follow the viewer's date preference, not the UI language — same rule as every
  // other date in the app (see `@/lib/format`). date-fns only does the bucket math.
  const labelFor = useMemo(() => {
    const locale = resolveDateLocale(dateFormat);
    const monthYear = new Intl.DateTimeFormat(locale, { month: "short", year: "numeric" });
    const monthDay = new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" });
    return (date: Date, granularity: Granularity) => (granularity === "month" ? monthYear : monthDay).format(date);
  }, [dateFormat]);
  const { buckets, neverCount, presentStatuses } = useMemo(
    () => buildChartData(users ?? [], labelFor),
    [users, labelFor],
  );
  const series = STATUS_SERIES.filter((s) => presentStatuses.includes(s.key));

  // Once the chart has data, let the intro play then turn animation off (resize won't re-animate).
  const chartReady = open && buckets.length > 0;
  useEffect(
    function freezeAnimationAfterIntro() {
      if (!chartReady) return;
      const timer = setTimeout(() => setAnimate(false), 800);
      return () => clearTimeout(timer);
    },
    [chartReady],
  );
  // Re-arm the intro animation for the next time the dialog opens.
  useEffect(
    function rearmAnimationOnClose() {
      if (!open) setAnimate(true);
    },
    [open],
  );

  function LastLoginTooltip({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: { dataKey?: string | number; value?: number }[];
    label?: string;
  }) {
    if (!active || !payload?.length) return null;
    const total = payload.reduce((sum, entry) => sum + (entry.value ?? 0), 0);
    return (
      <div className="min-w-32 rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md">
        <div className="font-semibold text-foreground">{label}</div>
        <div className="mt-1 space-y-0.5">
          {[...payload].reverse().map((entry) => {
            const s = STATUS_SERIES.find((x) => x.key === entry.dataKey);
            if (!s) return null;
            return (
              <div key={s.key} className="flex items-center gap-2">
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: s.color }} />
                <span className="text-muted-foreground">{statusLabel(s.key)}</span>
                <span className="ml-auto font-medium text-foreground">{COMPACT_NUMBER.format(entry.value ?? 0)}</span>
              </div>
            );
          })}
          {payload.length > 1 && (
            <div className="mt-1 flex items-center gap-2 border-t pt-1">
              <span className="text-muted-foreground">{t("users.lastLoginChart.tooltipTotal")}</span>
              <span className="ml-auto font-medium text-foreground">{COMPACT_NUMBER.format(total)}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[75vw] sm:max-w-[75vw]">
        <DialogHeader>
          <DialogTitle>{t("users.lastLoginChart.title")}</DialogTitle>
          <DialogDescription>{t("users.lastLoginChart.description")}</DialogDescription>
        </DialogHeader>

        <div className="h-[calc(75vh_-_10rem)] min-h-64 w-full">
          {isLoading ? (
            <InlineSpinner className="h-full" />
          ) : buckets.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              {t("users.lastLoginChart.empty")}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 800, height: 400 }}>
              <AreaChart accessibilityLayer={false} data={buckets} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <defs>
                  {series.map((s) => (
                    <linearGradient key={s.gradientId} id={s.gradientId} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={s.color} stopOpacity={0.9} />
                      <stop offset="95%" stopColor={s.color} stopOpacity={0.25} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                  tickLine={false}
                  axisLine={{ stroke: "var(--border)" }}
                  interval="preserveStartEnd"
                  minTickGap={16}
                />
                <YAxis
                  allowDecimals={false}
                  width={32}
                  tickFormatter={(value: number) => COMPACT_NUMBER.format(value)}
                  tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  cursor={{ stroke: "var(--muted-foreground)", strokeOpacity: 0.3 }}
                  content={<LastLoginTooltip />}
                />
                {series.map((s) => (
                  <Area
                    key={s.key}
                    dataKey={s.key}
                    name={statusLabel(s.key)}
                    type="monotone"
                    stackId="status"
                    fill={`url(#${s.gradientId})`}
                    fillOpacity={1}
                    stroke={s.color}
                    strokeWidth={2}
                    isAnimationActive={animate}
                    animationDuration={700}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Always rendered (with a reserved height) so the dialog doesn't resize when the chart and
            its footer appear after loading. */}
        <div className="flex min-h-5 flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
          {neverCount > 0 && <span>{t("users.lastLoginChart.neverNote", { count: neverCount })}</span>}
          {series.length > 1 && (
            <div className="ml-auto flex flex-wrap items-center gap-4">
              {[...series].reverse().map((s) => (
                <div key={s.key} className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
                  {statusLabel(s.key)}
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
